import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { AttributeSelectorService } from 'src/app/reusables/attribute-selector/attribute-selector.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { IModuleCustomSettingSection, ModuleCustomSettingSectionType } from 'src/shared/models/module-custom-settings-section.model';
import { DashboardModuleResponsiveInstance, ModuleInfo } from 'src/shared/models/module-info.model';
import { ModuleManager } from 'src/shared/models/module-manager';
import { IModuleManagerSettingsComponent, ModuleManagerGenericSettingsControls, ModuleManagerMetricInfo, ModuleManagerModuleSettings, ModuleSettingsView } from 'src/shared/models/module-manager/module-manager-settings.models';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { ModuleContainerService } from '../module-container/module-container.service';
import { MetricSelectorComponent } from 'src/app/reusables/metric-selector/metric-selector.component';
import { MetricSelectorService } from 'src/app/reusables/metric-selector/metric-selector.service';
import { ClearViewUtils } from 'src/shared/utils';

@Component({
    selector: 'cv-module-legacy-settings-modal',
    templateUrl: './module-legacy-settings-modal.component.html',
    styleUrls: ['./module-legacy-settings-modal.component.scss']
})
export class ModuleLegacySettingsModalComponent implements OnInit, OnDestroy, AfterViewInit, IModuleManagerSettingsComponent {
    @Input() moduleManager: ModuleManager;
    @Input() module: DashboardModuleResponsiveInstance;
    @Input() moduleInfo: ModuleInfo;
    @Output() settingsApplied = new EventEmitter<void>();
    @Output() settingsCancelled = new EventEmitter<void>();
    @Output() settingsViewChanged = new EventEmitter<IModuleManagerSettingsComponent>();

    //#region Embeded mode bindings
    // Render the settings as an embeded component
    @Input() embedMode: boolean = false;
    // Triggers the save event for embeded mode
    private _triggerApplySubscription: Subscription;
    @Input() set triggerApply(value: EventEmitter<void>) {
        if (this._triggerApplySubscription)
            this._triggerApplySubscription.unsubscribe();
        this._triggerApplySubscription = value.subscribe(this.applySettings.bind(this));
    }

    @Output() settingsFailed = new EventEmitter<void>();
    //#endregion Embeded mode bindings

    @ViewChild('empSelectorHolder', { read: ViewContainerRef }) vcrEmpSelectorHolder: ViewContainerRef;
    @ViewChild('recSelectorHolder', { read: ViewContainerRef }) vcrRecSelectorHolder: ViewContainerRef;
    @ViewChild('metricSelectorHolder', { read: ViewContainerRef }) vcrMetricSelectorHolder: ViewContainerRef;
    @ViewChild('attrSelectorHolder', { read: ViewContainerRef }) vcrAttrSelectorHolder: ViewContainerRef;
    @ViewChild('dateRangeSelectorHolder', { read: ViewContainerRef }) vcrDateRangeSelectorHolder: ViewContainerRef;
    @ViewChild('timeRangeSelectorHolder', { read: ViewContainerRef }) vcrTimeRangeSelectorHolder: ViewContainerRef;
    @ViewChild('intervalSelectorHolder', { read: ViewContainerRef }) vcrIntervalSelectorHolder: ViewContainerRef;
    @ViewChild('customSettingsTop', { read: ViewContainerRef }) vcrCustomSettingsTop: ViewContainerRef;
    @ViewChild('customSettingsAboveGroups', { read: ViewContainerRef }) vcrCustomSettingsAboveGroups: ViewContainerRef;
    @ViewChild('customSettingsBottom', { read: ViewContainerRef }) vcrCustomSettingsBottom: ViewContainerRef;

    hasMetricSelector = false;
    hasDateSelector: boolean;
    hasGroupSelector: boolean;
    hasAttributeFilters: boolean;
    hasIntervalSelector: boolean;
    showSelectMetrics: boolean;
    genericSettingsLoading = true;
    genericSettingsLoaded = false;
    settingsManager?: SlideoutSettingsManager;

    private _subscription: Subscription;

    private _settingsLoading = false;

    constructor(
        private element: ElementRef<HTMLElement>,
        private cvLocalizationService: CVLocalizationService,
        private moduleContainerService: ModuleContainerService,
        private attributeSelectorService: AttributeSelectorService,
        private cd: ChangeDetectorRef,
        private metricSelectorService: MetricSelectorService,
    ) { }
    
    get settingsLoading() { return this._settingsLoading; };
    set settingsLoading(val: boolean) {
        this._settingsLoading = val;
        this.cd.detectChanges();
    }
    
    
    async ngOnInit() {
        const localizationPromises = [
            this.cvLocalizationService.GetSection('Module'),
            this.cvLocalizationService.GetSection('Subscriptions_Dashboard'),
        ];

        await Promise.all(localizationPromises);

        // handle jQuery event for metric selector because Angular can only handle native events
        $(document).on('event_SelectedMetricsChanged', this.onSelectedMetricsChangedHandler);
        this.settingsManager = new SlideoutSettingsManager(this.attributeSelectorService, this.module);
        this._subscription = this.settingsManager.onPlaceCustomSettings.subscribe(component => this.placeCustomSettingsSection(component));
    }

    ngAfterViewInit() {
        this.settingsViewChanged.emit(this);
        setTimeout(() => this.moduleManager?.slideoutSettingsManager.next(this.settingsManager));
    }

    ngOnDestroy() {
        $(document).off('event_SelectedMetricsChanged', this.onSelectedMetricsChangedHandler);
        this._subscription?.unsubscribe();
        this._triggerApplySubscription?.unsubscribe();
    }

    /**
     * Handles metric change events fired from the metric selector
     *
     * @param e The event fired from the metric selector.
     */
    onSelectedMetricsChanged(e) {
        if (!e.result && e.pageName === (this.moduleManager.getModuleKey() + '_temp')) {
            this.settingsManager.selectedMetrics = [];
            for (const metricId of e.selectedMetrics) {
                this.settingsManager.selectedMetrics.push({
                    Id: metricId,
                    Abbreviation: e.metricNames[metricId],
                    Name: e.metricFullNames[metricId],
                    ConversionType: parseInt(e.mCT[metricId], 10),
                    Direction: parseInt(e.mCT[metricId], 10),
                    MetricType: parseInt(e.metricTypes[metricId], 10),
                });
            }

            if (this.settingsManager.selectedMetrics.length > 0) {
                this.showSelectMetrics = false;
            }
        }
    }

    /**
     * Gets a newline separated list of metrics after the first 5 metrics
     *
     * @returns
     */
    getMoreMetricsList() {
        if (this.settingsManager.selectedMetrics.length <= 5) {
            return '';
        }

        return this.settingsManager.selectedMetrics.reduce((metricListStr, metric) => metricListStr + metric.Name + '\n', '');
    }

    placeCustomSettingsSection(component: IModuleCustomSettingSection) {
        if (component.type !== ModuleCustomSettingSectionType.Legacy)
            return;

        let settingsContainer: HTMLElement;
        switch (component.position) {
            case 'top':
                settingsContainer = this.vcrCustomSettingsTop.element.nativeElement;
                break;
            case 'above-groups':
                settingsContainer = this.vcrCustomSettingsAboveGroups.element.nativeElement;
                break;
            case 'bottom':
                settingsContainer = this.vcrCustomSettingsBottom.element.nativeElement;
                break;
            default:
                console.error(`Invalid custom settings position ${component.position}`);
                return;
        }

        settingsContainer.appendChild(component.getNativeElement());
    }

    setGenericSettingsControlSettings(controlSettings: ModuleManagerModuleSettings): void {
        this.settingsManager.selectedMetrics = controlSettings.selectedMetrics ?? [];
    }

    setGenericSettingsHtml(data: ModuleManagerGenericSettingsControls): void {
        const {
            empSelectorHTML,
            recSelectorHTML,
            metricSelectorHTML,
            attrSelectorHTML,
            dateRangeSelectorHTML,
            timeRangeSelectorHTML,
            intervalSelectorHTML,
            controlSettings,
        } = data;

        this.genericSettingsLoading = false;
        this.genericSettingsLoaded = true;

        // read selected metrics from metric selector html
        this.settingsManager.selectedMetrics = [];
        if (metricSelectorHTML) {
            this.hasMetricSelector = true;
            this.settingsManager.selectedMetrics = controlSettings.selectedMetrics;
            const abbreviations =
                this.settingsManager.selectedMetrics.map(m => m.CustomName && m.CustomName !== "" ? m.CustomName : m.Abbreviation);
            this.settingsManager.selectedMetricsText = ClearViewUtils.joinWithMax(abbreviations, 40, ', ');
        }

        this.vcrEmpSelectorHolder.element.nativeElement.innerHTML = empSelectorHTML ?? '';
        this.vcrRecSelectorHolder.element.nativeElement.innerHTML = recSelectorHTML ?? '';
        this.vcrAttrSelectorHolder.element.nativeElement.innerHTML = attrSelectorHTML ?? '';
        this.vcrDateRangeSelectorHolder.element.nativeElement.innerHTML = dateRangeSelectorHTML ?? '';
        this.vcrTimeRangeSelectorHolder.element.nativeElement.innerHTML = timeRangeSelectorHTML ?? '';
        this.vcrIntervalSelectorHolder.element.nativeElement.innerHTML = intervalSelectorHTML ?? '';

        if(this.embedMode)

        //ng-hide false if selector is applicable to the module.
        if (dateRangeSelectorHTML) {
            this.hasDateSelector = true;
        }

        if (empSelectorHTML) {
            this.hasGroupSelector = true;
        }

        if (attrSelectorHTML) {
            this.hasAttributeFilters = true;
        }

        if (intervalSelectorHTML) {
            this.hasIntervalSelector = true;
        }

        if (timeRangeSelectorHTML)
            window['initSlider']($(this.element.nativeElement), this.cvLocalizationService.getCachedText(
                "Subscriptions_Dashboard", "To", "to"));

        // because this can be called from AngularJS
        this.cd.detectChanges();
    }

    getSelectedDateRange() {
        const moduleKey = this.moduleManager.getModuleKey();
        const dateRangePickerSelector = `div.DateRangePickerContainer[pagename="${moduleKey}_temp"]`;
        let txtDateRange: HTMLDivElement = document.querySelector(`${dateRangePickerSelector} .txtDateRange:not(.responsive)`);

        if (txtDateRange == null || txtDateRange.offsetParent == null) // offsetParent null means not visible (settings not open)
            txtDateRange = document.querySelector(`${dateRangePickerSelector} .txtDateRange.responsive`); // try for front picker

        if (txtDateRange == null)
            return 0; // no date range picker, just ignore for now

        const selectedDateRange = txtDateRange.textContent;
        if (selectedDateRange.includes(' - ')) { // custom...
            const dateFormat = window['CVAngularDateFormat'];
            const startEnd = selectedDateRange.split(' - ');
            const startDate = Date.parseExact(startEnd[0], dateFormat);
            const endDate = Date.parseExact(startEnd[1], dateFormat);
            const dayDiff = startDate.getElapsed(endDate) / 1000 / 60 / 60 / 24;
            return dayDiff;
        }

        switch (selectedDateRange) {
            case "Today":
            case "Yesterday":
                return 1;
            case "This Week":
            case "Last 7 Days":
            case "Last Week":
                return 7;
            case "Previous 15":
            case "Current 15":
                return 15;
            case "Last 4 Weeks":
                return 28;
            case "This Month":
            case "Last 30 Days":
            case "Last Month":
                return 30;
            case "Last 8 Weeks":
                return 56;
            case "Last 90 Days":
                return 90;
            case "This Quarter":
            case "Last Quarter":
                return 92;
            case "YTD":
            case "Rolling Year":
            case "Last Year":
                return 365;
            default:
                return 0;
        }
    }

    changeSettingsView() {
        // change to slideout view
        this.moduleContainerService.currentSettingsView = ModuleSettingsView.Slideout;
    }

    applySettings() {
        if (!this.moduleManager) {
            this.settingsFailed.emit();
            return;
        }
        // If date selection is greater then the maximum, do not apply settings and show error message
        const daterange = this.getSelectedDateRange();
        if (daterange > this.moduleManager.maxDateRange) {
            window['showErrorDialog'](this.cvLocalizationService.Format(
                this.cvLocalizationService.getCachedText(
                    'Module',
                    'MaxDateRange',
                    `Date range larger than the maximum date range of {0} days.`
                ),
                this.moduleManager.maxDateRange
            ));
            this.settingsFailed.emit();
            return;
        }

        // force user to select metrics
        if (this.hasMetricSelector && this.settingsManager.selectedMetrics.length <= 0) {
            this.showSelectMetrics = true;
            this.settingsFailed.emit();
            return;
        }

        this.settingsApplied.emit();
    }

    cancelSettings() {
        this.showSelectMetrics = false;
        this.settingsCancelled.emit();
    }

    openMetricSelector() {
        const curSelectedMetrics = this.settingsManager.selectedMetrics;
        const metricSelector = this.metricSelectorService.openModal(MetricSelectorComponent, this.moduleManager, curSelectedMetrics, true);
        const newSelectedMetrics = [];
        metricSelector.result.then((metricSelectorResult) => {
            if (metricSelectorResult) {
                metricSelectorResult?.forEach(m => {
                    newSelectedMetrics.push({
                        ...m,
                        Id: m.MetricID,
                        Abbreviation: m.Abbreviation,
                        Name: m.MetricName,
                        ConversionType: m.ConversionType,
                        CustomName: m.MetricCustomName
                    });
                });
                this.settingsManager.selectedMetrics = newSelectedMetrics;
                this.settingsManager.updateSelectedMetricsText();
            }
        });
    }

    private onSelectedMetricsChangedHandler = (e: any) => this.onSelectedMetricsChanged(e);
}
